import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, type Account, type Entity } from "../lib/api";
import { SECTIONS } from "../lib/nav";
import { useScrollLock } from "../lib/scrollLock";

/**
 * The global search (web/DESIGN.md §5): ⌘K / Ctrl+K on every page, "find
 * anything that mentions".
 *
 * What it finds: every page in the navigation, every account, every entity --
 * and, for anything else, one row that runs the query as a full search of the
 * bank transactions, which is the screen that already searches every column
 * (counterparty, description, amount as written) on the server.
 *
 * The query survives closing and reopening (it lives in the shell), so a
 * search set once holds until it is cleared.
 *
 * ⚠️ Accounts and entities are fetched when the palette opens, not when the
 * app loads -- a person who never presses ⌘K pays nothing. The last list is
 * kept for the session so a second open is instant and refreshes underneath.
 */

export interface SearchHit {
  key: string;
  group: "Pages" | "Accounts" | "Entities" | "Search";
  label: string;
  detail?: string;
  to: string;
}

let cache: { accounts: Account[]; entities: Entity[] } | null = null;

const PAGES: SearchHit[] = SECTIONS.flatMap((section) =>
  section.tabs.length === 0
    ? [{ key: `p:${section.to}`, group: "Pages" as const, label: section.label, to: section.to }]
    : section.tabs.map((tab) => ({
        key: `p:${tab.to}`,
        group: "Pages" as const,
        label: tab.label,
        detail: section.label,
        to: tab.to,
      })),
);

const PER_GROUP = 6;

/** Where `needle` lands in `hay`: 0 = starts with it, 1 = a word starts with it,
 *  2 = anywhere, null = not at all. Lower ranks first. */
function rank(hay: string, needle: string): number | null {
  const h = hay.toLowerCase();
  const i = h.indexOf(needle);
  if (i < 0) return null;
  if (i === 0) return 0;
  return /[\s:\-/(]/.test(h[i - 1]) ? 1 : 2;
}

export function searchHits(
  query: string,
  data: { accounts: Account[]; entities: Entity[] } | null,
): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return PAGES.slice(0, 8);

  function pick<T>(items: T[], fields: (item: T) => string[], toHit: (item: T) => SearchHit) {
    const scored: { hit: SearchHit; score: number }[] = [];
    for (const item of items) {
      let best: number | null = null;
      for (const field of fields(item)) {
        const r = rank(field, q);
        if (r !== null && (best === null || r < best)) best = r;
      }
      if (best !== null) scored.push({ hit: toHit(item), score: best });
    }
    scored.sort((a, b) => a.score - b.score || a.hit.label.localeCompare(b.hit.label));
    return scored.slice(0, PER_GROUP).map((s) => s.hit);
  }

  const pages = pick(PAGES, (p) => [p.label, p.detail ?? ""], (p) => p);
  const accounts = data
    ? pick(
        data.accounts.filter((a) => !a.retired_on),
        (a) => [a.name, a.entity_name, a.institution_name],
        (a) => ({
          key: `a:${a.id}`,
          group: "Accounts",
          label: a.name,
          detail: `${a.entity_name} · ${a.institution_name}`,
          to: `/accounts/${a.id}`,
        }),
      )
    : [];
  const entities = data
    ? pick(
        data.entities.filter((e) => !e.retired_on),
        (e) => [e.name],
        (e) => ({
          key: `e:${e.id}`,
          group: "Entities",
          label: e.name,
          detail: `${e.account_count} account${e.account_count === 1 ? "" : "s"}`,
          to: `/entities/${e.id}`,
        }),
      )
    : [];

  return [
    ...pages,
    ...accounts,
    ...entities,
    {
      key: "s:transactions",
      group: "Search",
      label: `Bank transactions mentioning “${query.trim()}”`,
      to: `/transactions?search=${encodeURIComponent(query.trim())}`,
    },
  ];
}

export default function SearchPalette({
  query,
  onQuery,
  onClose,
}: {
  query: string;
  onQuery: (next: string) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [data, setData] = useState(cache);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let live = true;
    void Promise.allSettled([api.accounts(), api.entities()]).then(([a, e]) => {
      if (!live) return;
      cache = {
        accounts: a.status === "fulfilled" ? a.value.data.accounts : (cache?.accounts ?? []),
        entities: e.status === "fulfilled" ? e.value.data.entities : (cache?.entities ?? []),
      };
      setData(cache);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  useScrollLock();

  const hits = useMemo(() => searchHits(query, data), [query, data]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function go(hit: SearchHit) {
    onClose();
    navigate(hit.to);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, hits.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = hits[active];
      if (hit) go(hit);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  let lastGroup = "";
  return createPortal(
    <div
      className="palette-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label="Search">
        <div className="palette-field">
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.4 10.4 14 14" />
          </svg>
          <input
            ref={input}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search"
            aria-label="Search"
            aria-controls="palette-list"
            aria-activedescendant={hits[active] ? `palette-${active}` : undefined}
            role="combobox"
            aria-expanded="true"
            spellCheck={false}
            autoComplete="off"
          />
          {query ? (
            <button type="button" className="link small" onClick={() => onQuery("")}>
              Clear
            </button>
          ) : null}
          <kbd>Esc</kbd>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" ref={list}>
          {hits.map((hit, i) => {
            const head = hit.group !== lastGroup ? hit.group : null;
            lastGroup = hit.group;
            return (
              <div key={hit.key}>
                {head ? <div className="palette-group">{head}</div> : null}
                <div
                  id={`palette-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={i === active}
                  className={`palette-item ${i === active ? "active" : ""}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(hit)}
                >
                  <span className="palette-label">{hit.label}</span>
                  {hit.detail ? <span className="palette-detail">{hit.detail}</span> : null}
                </div>
              </div>
            );
          })}
          {query.trim() && data === null ? (
            <div className="palette-group">Loading accounts…</div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
